import { useEffect, Suspense } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Navigation } from "@/components/Navigation";
import { HeroSection } from "@/components/HeroSection";
import { Footer } from "@/components/Footer";
import { useTranslation } from "react-i18next";
import { lazyWithRetry } from "@/lib/lazyWithRetry";

// Lazy load below-the-fold components to improve TTI
const StudentCommunityBanner = lazyWithRetry(() => import("@/components/StudentCommunityBanner").then(m => ({ default: m.StudentCommunityBanner })));
const CallToActionSection = lazyWithRetry(() => import("@/components/CallToActionSection").then(m => ({ default: m.CallToActionSection })));
const ProductLinesSummary = lazyWithRetry(() => import("@/components/ProductLinesSummary").then(m => ({ default: m.ProductLinesSummary })));
const ServicesSummary = lazyWithRetry(() => import("@/components/ServicesSummary").then(m => ({ default: m.ServicesSummary })));
const ReviewsSection = lazyWithRetry(() => import("@/components/ReviewsSection").then(m => ({ default: m.ReviewsSection })));
const AppointmentSummary = lazyWithRetry(() => import("@/components/AppointmentSummary").then(m => ({ default: m.AppointmentSummary })));
const TariffePricingSection = lazyWithRetry(() => import("@/components/TariffePricingSection").then(m => ({ default: m.TariffePricingSection })));
const SocialWelcomeBlock = lazyWithRetry(() => import("@/components/SocialWelcomeBlock").then(m => ({ default: m.SocialWelcomeBlock })));
const CityRentalsSection = lazyWithRetry(() => import("@/components/CityRentalsSection").then(m => ({ default: m.CityRentalsSection })));


// Minimal skeleton for lazy components
const SectionSkeleton = () => <div className="animate-pulse bg-muted/30 rounded-2xl h-48" />;

const SOCIAL_UTM_SOURCES = ["facebook", "instagram", "fb", "ig"];

const Index = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Redirect to /social for users coming from Facebook/Instagram
  useEffect(() => {
    const utmSource = searchParams.get("utm_source")?.toLowerCase();
    const referrer = document.referrer.toLowerCase();
    
    const isFromSocialUtm = utmSource && SOCIAL_UTM_SOURCES.some(s => utmSource.includes(s));
    const isFromSocialReferrer = referrer.includes("facebook") || referrer.includes("instagram");
    
    if (isFromSocialUtm || isFromSocialReferrer) {
      // Preserve UTM params in redirect
      const params = searchParams.toString();
      navigate(`/social${params ? `?${params}` : ""}`, { replace: true });
    }
  }, [searchParams, navigate]);

  // FAQ Structured Data for SEO
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": t('faqStructured.q1'),
        "acceptedAnswer": {
          "@type": "Answer",
          "text": t('faqStructured.a1')
        }
      },
      {
        "@type": "Question",
        "name": t('faqStructured.q2'),
        "acceptedAnswer": {
          "@type": "Answer",
          "text": t('faqStructured.a2')
        }
      },
      {
        "@type": "Question",
        "name": t('faqStructured.q3'),
        "acceptedAnswer": {
          "@type": "Answer",
          "text": t('faqStructured.a3')
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{i18n.language === 'it' ? 'Affitti Sicuri tra Privati Verificati a Milano e Napoli | Domus Flexi' : 'Secure Rentals Between Verified Individuals in Milan and Naples | Domus Flexi'}</title>
        <meta name="description" content={i18n.language === 'it' ? 'Domus Flexi: la piattaforma che tutela proprietari e inquilini. Affitti sicuri senza agenzia a Milano e Napoli. Utenti verificati, privacy protetta.' : 'Domus Flexi: the platform that protects landlords and tenants. Secure rentals without agency in Milan and Naples. Verified users, protected privacy.'} />
        <link rel="canonical" href="https://domusflexi.com/" />
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
      </Helmet>
      <Navigation />
      <HeroSection />
      <Suspense fallback={<SectionSkeleton />}>
        <StudentCommunityBanner />
        <SocialWelcomeBlock />
      </Suspense>
      
      {/* Info Boxes Grid */}
      <section className="py-12 px-4 bg-background">
        <div className="container mx-auto max-w-6xl">
          <Suspense fallback={<div className="grid grid-cols-1 md:grid-cols-2 gap-6"><SectionSkeleton /><SectionSkeleton /><SectionSkeleton /><SectionSkeleton /></div>}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card border border-border rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow">
                <CallToActionSection />
              </div>
              <div className="bg-card border border-border rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow">
                <ProductLinesSummary />
              </div>
              <div className="bg-card border border-border rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow">
                <TariffePricingSection />
              </div>
              <div className="bg-card border border-border rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow">
                <CityRentalsSection />
              </div>
              <div className="bg-card border border-border rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow md:col-span-2">
                <ServicesSummary />
              </div>
            </div>
          </Suspense>
          
          {/* Sezione Navigazione Principale - 4 Box Informativi */}
          <div className="mt-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">{t('explore.title')}</h2>
              <p className="text-muted-foreground">{t('explore.subtitle')}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Box 1 - Chi è Domus Flexi */}
              <a href="/chi-siamo" className="block group">
                <div className="bg-gradient-to-br from-primary/5 to-accent/5 border border-primary/20 rounded-2xl p-6 shadow-md hover:shadow-lg transition-all hover:border-primary/40 h-full">
                  <div className="flex items-start gap-4">
                    <div className="bg-primary/10 rounded-xl p-3 flex-shrink-0">
                      <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-colors">{t('explore.aboutTitle')}</h3>
                      <p className="text-sm text-muted-foreground mb-3">{t('explore.aboutDesc')}</p>
                      <span className="inline-flex items-center text-primary font-semibold text-sm group-hover:underline">
                        {t('explore.aboutLink')}
                      </span>
                    </div>
                  </div>
                </div>
              </a>

              {/* Box 2 - Come funziona Domus Flexi */}
              <a href="/come-funziona" className="block group">
                <div className="bg-gradient-to-br from-accent/5 to-primary/5 border border-accent/20 rounded-2xl p-6 shadow-md hover:shadow-lg transition-all hover:border-accent/40 h-full">
                  <div className="flex items-start gap-4">
                    <div className="bg-accent/20 rounded-xl p-3 flex-shrink-0">
                      <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-colors">{t('explore.howTitle')}</h3>
                      <p className="text-sm text-muted-foreground mb-3">{t('explore.howDesc')}</p>
                      <span className="inline-flex items-center text-primary font-semibold text-sm group-hover:underline">
                        {t('explore.howLink')}
                      </span>
                    </div>
                  </div>
                </div>
              </a>

              {/* Box 3 - Sicurezza e Verifiche */}
              <a href="/sicurezza-verifiche" className="block group">
                <div className="bg-gradient-to-br from-primary/5 to-accent/5 border border-primary/20 rounded-2xl p-6 shadow-md hover:shadow-lg transition-all hover:border-primary/40 h-full">
                  <div className="flex items-start gap-4">
                    <div className="bg-primary/10 rounded-xl p-3 flex-shrink-0">
                      <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-colors">{t('explore.securityTitle')}</h3>
                      <p className="text-sm text-muted-foreground mb-3">{t('explore.securityDesc')}</p>
                      <span className="inline-flex items-center text-primary font-semibold text-sm group-hover:underline">
                        {t('explore.securityLink')}
                      </span>
                    </div>
                  </div>
                </div>
              </a>

              {/* Box 4 - Affitti in Italia */}
              <a href="/affitti-italia" className="block group">
                <div className="bg-gradient-to-br from-accent/5 to-primary/5 border border-accent/20 rounded-2xl p-6 shadow-md hover:shadow-lg transition-all hover:border-accent/40 h-full">
                  <div className="flex items-start gap-4">
                    <div className="bg-accent/20 rounded-xl p-3 flex-shrink-0">
                      <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-colors">{t('explore.italyTitle')}</h3>
                      <p className="text-sm text-muted-foreground mb-3">{t('explore.italyDesc')}</p>
                      <span className="inline-flex items-center text-primary font-semibold text-sm group-hover:underline">
                        {t('explore.italyLink')}
                      </span>
                    </div>
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Final Section: Questions & Reviews */}
      <section className="py-12 px-4 bg-background">
        <div className="container mx-auto max-w-6xl">
          <Suspense fallback={<div className="grid grid-cols-1 lg:grid-cols-2 gap-6"><SectionSkeleton /><SectionSkeleton /></div>}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-card border border-border rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow">
                <AppointmentSummary />
              </div>
              <div className="bg-card border border-border rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow">
                <div className="text-center mb-8">
                  <h3 className="text-2xl md:text-3xl font-bold text-primary mb-4">
                    {t("reviews.title")}
                  </h3>
                  <p className="text-muted-foreground">
                    {t("reviews.description")}
                  </p>
                </div>
                <ReviewsSection />
              </div>
            </div>
          </Suspense>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Index;